import React from 'react';
import {
  DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Select,
  Typography, IconButton, Box, Stack, Divider, Alert, CircularProgress,
  FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { StateManager } from '../utilities/stateManager';
import Store from '../utilities/store';
import RequestManager from '../utilities/requestManager';
import { StackedDialog, useSelectMenuProps } from '../utilities/ModalStackContext';

const usd = (val) =>
  (Number(val) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const ACCOUNT_TYPES = [
  { value: 'personalChecking', label: 'Personal Checking' },
  { value: 'personalSavings', label: 'Personal Savings' },
  { value: 'businessChecking', label: 'Business Checking' },
  { value: 'businessSavings', label: 'Business Savings' },
];

// Global admin-only "pay this NTO out of Mercury" dialog, opened via
// StateManager.openPayNtoModal(payable). The amount is not editable — the
// backend always pays the car's NTO (updated_nto first), so the number on
// screen and the number sent cannot drift apart.
export default function PayNtoModal({ open, payable, onClose }) {
  const [method, setMethod] = React.useState('ach');
  const [accountType, setAccountType] = React.useState('personalChecking');
  const [accountId, setAccountId] = React.useState('');
  const [accounts, setAccounts] = React.useState([]);
  const [memo, setMemo] = React.useState('');
  const [consignor, setConsignor] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const menuProps = useSelectMenuProps();

  const stock = payable && payable.stock;
  const purchaseId = payable && payable.id;

  // Read the consignor's payout readiness and the Mercury accounts together, so
  // the dialog can refuse a send that Mercury would only reject later.
  React.useEffect(() => {
    if (!open || !stock) return;
    let cancelled = false;
    setLoading(true);
    setConsignor(null);
    setMethod('ach');
    setMemo(`NTO ${stock}`);

    (async () => {
      const [info, res] = await Promise.all([
        Store.getConsignorPayoutInfo(stock),
        RequestManager.get({ function: 'mercuryListAccounts' }),
      ]);
      if (cancelled) return;

      setConsignor(info);
      const list = (res && res.accounts) || [];
      setAccounts(list);
      const preferred = list.find(a => a.kind === 'checking') || list[0];
      setAccountId(preferred ? preferred.id : '');
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [open, stock, purchaseId]);

  if (!StateManager.isAdmin()) return null;

  const amount = (consignor && consignor.amount) || (payable && (payable.updated_nto || payable.amount)) || 0;
  const missing = (consignor && consignor.missing) || [];
  const blocked = !!(consignor && consignor.error) || missing.length > 0;
  const canSend = !loading && !sending && !blocked && !!accountId && amount > 0;
  const account = accounts.find(a => a.id === accountId);

  const send = async () => {
    setSending(true);
    StateManager.setLoading(true);
    try {
      const res = await RequestManager.post({
        function: 'mercuryPayConsignor',
        variables: { purchaseId, paymentMethod: method, accountType, accountId, memo },
      });

      if (!res || res.success === false) {
        const detail = res && res.error;
        const message = typeof detail === 'string'
          ? detail
          : (detail && (detail.message || JSON.stringify(detail))) || 'Mercury rejected the payment';
        throw new Error(message);
      }

      StateManager.setAlertAndOpen(
        `Sent ${usd(res.amount)} to ${(consignor && consignor.name) || 'the consignor'} — pending in Mercury`,
        'success',
      );
      if (typeof StateManager.updateData === 'function') StateManager.updateData();
      onClose();
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen(`Could not send the payment: ${err.message}`, 'error');
    } finally {
      setSending(false);
      StateManager.setLoading(false);
    }
  };

  return (
    <StackedDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Pay NTO</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={20} />
            <Typography color="text.secondary">Checking the consignor and your Mercury accounts…</Typography>
          </Stack>
        )}

        {!loading && consignor && consignor.error && (
          <Alert severity="error" sx={{ mb: 2 }}>{consignor.error}</Alert>
        )}

        {!loading && consignor && !consignor.error && missing.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Mercury needs more on {consignor.name || 'the consignor'}'s customer record first:{' '}
            <b>{missing.join(', ')}</b>.
            {consignor.customerId && (
              <Button
                size="small"
                sx={{ ml: 1 }}
                onClick={() => StateManager.openCustomerModal(consignor.customerId)}
              >
                Open customer
              </Button>
            )}
          </Alert>
        )}

        {!loading && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">Car</Typography>
              <Typography>{(payable && payable.car) || stock}</Typography>
            </Box>

            <Stack direction="row" spacing={3}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">Payee</Typography>
                <Typography>{(consignor && consignor.name) || (payable && payable.vendor) || '—'}</Typography>
                {consignor && consignor.last4 && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Account ····{consignor.last4}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Amount</Typography>
                <Typography variant="h6" fontWeight={700}>{usd(amount)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {consignor && consignor.usedUpdatedNto ? 'Updated NTO' : 'NTO'}
                </Typography>
              </Box>
            </Stack>

            <Divider />

            {accounts.length === 0 ? (
              <Alert severity="error">
                Could not read your Mercury accounts. Check that the Mercury API token is valid
                and that {'35.193.83.7'} is on its IP allowlist.
              </Alert>
            ) : (
              <FormControl fullWidth size="small">
                <InputLabel id="mercury-account-label">Send from</InputLabel>
                <Select
                  labelId="mercury-account-label"
                  label="Send from"
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  MenuProps={menuProps}
                >
                  {accounts.map(a => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name} ····{a.last4} — {usd(a.availableBalance)} available
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {account && amount > (account.availableBalance || 0) && (
              <Alert severity="warning">
                {usd(amount)} is more than the {usd(account.availableBalance)} available in {account.name}.
              </Alert>
            )}

            <FormControl>
              <FormLabel sx={{ fontSize: 13 }}>Method</FormLabel>
              <RadioGroup value={method} onChange={(e) => setMethod(e.target.value)}>
                <FormControlLabel
                  value="ach" control={<Radio size="small" />}
                  label="ACH — no fee, 1–3 business days"
                />
                <FormControlLabel
                  value="domesticWire" control={<Radio size="small" />}
                  label="Domestic wire — fee applies, same day"
                />
              </RadioGroup>
            </FormControl>

            {/* Only asked when we are about to create the Mercury recipient.
                An existing recipient already carries its account type. */}
            {method === 'ach' && consignor && !consignor.hasRecipient && (
              <FormControl fullWidth size="small">
                <InputLabel id="account-type-label">Their account type</InputLabel>
                <Select
                  labelId="account-type-label"
                  label="Their account type"
                  value={accountType}
                  onChange={(e) => setAccountType(e.target.value)}
                  MenuProps={menuProps}
                >
                  {ACCOUNT_TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth size="small" label="Memo"
              value={memo} onChange={(e) => setMemo(e.target.value)}
            />

            <Typography variant="caption" color="text.secondary">
              Mercury creates the payment as pending. Its own approval rules still apply
              before the money leaves the account.
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <Button
          variant="contained" startIcon={<AccountBalanceIcon />}
          onClick={send} disabled={!canSend}
        >
          {sending ? 'Sending…' : `Send ${usd(amount)}`}
        </Button>
      </DialogActions>
    </StackedDialog>
  );
}
